import { Table, type TableProps } from 'antd'
import { useAtom } from 'jotai'
import {
  type FC,
  type ComponentPropsWithoutRef,
  useCallback,
  useMemo,
  useState
} from 'react'
import { useFormContext } from 'react-hook-form'
import { FaColumns, FaGripVertical } from 'react-icons/fa'

import { Button } from '@/components/elements'
import {
  ACTIONS,
  DESCRIPTIONS,
  LABELS
} from '@/constants/common/message.constants'
import { useCustomKeySettingsQuery } from '@/features/custom-key-settings/hooks'
import { useAntdTableSort } from '@/features/shared/hooks'
import { type Sorter } from '@/features/shared/models'
import { type ShipmentOrdersListExEntity } from '@/libs/api'
import { type ShipmentDeliveryStatusType } from '@/libs/api/types'

import {
  buildBundleSelectionStates,
  type ShipmentOrdersSearch
} from '../../-models'
import {
  EMPTY_COLUMN_LAYOUT,
  fitFixedColumns,
  type ResolvedColumn,
  resizeColumn,
  resolveColumnLayout,
  toColumnLayout
} from '../../-models/column-layout'
import { type ShipmentOrdersFormValues } from '../../-models/shipment-orders-form-values'
import { shipmentOrdersColumnLayoutAtomFamily } from '../../-states/atoms'
import { ShipmentOrdersColumnSettingsModal } from '../ShipmentOrdersColumnSettings'

import {
  applyColumnLayout,
  FIXED_WIDTH_MARGIN,
  sumVisibleWidth,
  toColumnLayoutDefinitions
} from './apply-column-layout'
import { DraggableHeaderCell } from './DraggableHeaderCell'
import { HeaderColumnDragArea } from './HeaderColumnDragArea'
import { createShipmentOrdersTableColumns } from './shipment-orders-table.columns'
import { useElementWidth } from './use-element-width'

/** 見出しが ReactNode の列の、列の設定に出す名前 */
const SETTING_LABEL_BY_KEY: Record<string, string> = {
  weight: LABELS.WEIGHT
}

const TABLE_COMPONENTS: TableProps['components'] = {
  header: { cell: DraggableHeaderCell }
}

type Props = ComponentPropsWithoutRef<'div'> & {
  tenantKey: string
  /** 列レイアウトの保存先（localStorage）をユーザー単位で分けるのに使う */
  userId: number
  /** 親でピン留め合成済みの dataSource（選択済みだが現ページに無い行 + 現ページの行） */
  shipmentOrders: ShipmentOrdersListExEntity[]
  search: ShipmentOrdersSearch
  onChangeSort: (sorter: Sorter) => void
  isEditing: boolean
  selectedRowKeys: React.Key[]
  onSelectionChange: (
    keys: React.Key[],
    rows: (ShipmentOrdersListExEntity | undefined)[]
  ) => void
  /** ページング・絞り込みを跨いで選択を維持するため先頭にピン留め表示している行の id 集合 */
  pinnedRowIds: Set<number>
  onStatusChange?: (
    id: number,
    deliveryStatus: ShipmentDeliveryStatusType
  ) => Promise<void>
  statusEditVariant?: 'clickable-tag' | 'tag-with-edit-icon'
  onClickOrderNo?: (record: ShipmentOrdersListExEntity) => void
  /** まとめキー左のチェックで、同じ束ねの依頼をまとめて行選択する（未指定ならチェックを出さない） */
  onToggleBundleSelection?: (record: ShipmentOrdersListExEntity) => void
  /**
   * sticky な列ヘッダをスクロール領域上端から何 px 下に貼り付けるか。
   * 選択中の一括操作バーが上に固定される分をここでずらす。
   * @default 0
   */
  stickyOffsetHeader?: number
}

export const ShipmentOrdersTableBody: FC<Props> = ({
  tenantKey,
  userId,
  shipmentOrders,
  search,
  onChangeSort,
  isEditing,
  selectedRowKeys,
  onSelectionChange,
  pinnedRowIds,
  onStatusChange,
  statusEditVariant,
  onClickOrderNo,
  onToggleBundleSelection,
  stickyOffsetHeader = 0
}) => {
  const handleTableChange =
    useAntdTableSort<ShipmentOrdersListExEntity>(onChangeSort)

  const sorter = useMemo<Sorter>(() => {
    return {
      sort: search.sort,
      order: search.order
    }
  }, [search.sort, search.order])

  const { control, register } = useFormContext<ShipmentOrdersFormValues>()

  // 編集中のみ有効な、選択順を保った安定 index マップ（dataSource 上の位置には依存しない）。
  // ピン留めで dataSource の並びが動的に変わっても、同じ行が常に同じフォーム配列 index に
  // 対応するようにするための仕組み
  const editableRowIndexById = useMemo(
    () =>
      isEditing
        ? new Map(selectedRowKeys.map((key, index) => [Number(key), index]))
        : new Map<number, number>(),
    [isEditing, selectedRowKeys]
  )

  // まとめキー左のチェックの表示状態。画面に出ている行（ピン留め済みの選択行を含む）を
  // 束ね単位に集計して求める。押下時の分岐（全選択 or 解除）も同じ集計を使う
  const bundleSelection = useMemo(() => {
    if (!onToggleBundleSelection) return undefined
    return {
      stateByBundleId: buildBundleSelectionStates(
        shipmentOrders,
        new Set(selectedRowKeys.map(Number))
      ),
      disabled: isEditing,
      onToggle: onToggleBundleSelection
    }
  }, [onToggleBundleSelection, shipmentOrders, selectedRowKeys, isEditing])

  // VOC_PJ-960 #4487: 拠点のカスタムキー設定（列ヘッダーのラベル解決 + 表示スロット判定）
  const { data: customKeySettings } = useCustomKeySettingsQuery(
    tenantKey,
    search.dcId
  )

  const baseColumns = useMemo(
    () =>
      createShipmentOrdersTableColumns(
        tenantKey,
        sorter,
        editableRowIndexById,
        control,
        register,
        onStatusChange,
        statusEditVariant,
        onClickOrderNo,
        bundleSelection,
        customKeySettings
      ),
    [
      tenantKey,
      sorter,
      editableRowIndexById,
      control,
      register,
      onStatusChange,
      statusEditVariant,
      onClickOrderNo,
      bundleSelection,
      customKeySettings
    ]
  )

  // #3690 VOC_PJ-577: 列レイアウト（並び・幅・表示/非表示・固定）をユーザー単位で保存し、列定義に当てる
  const [columnLayout, setColumnLayout] = useAtom(
    shipmentOrdersColumnLayoutAtomFamily({ tenantKey, userId })
  )
  const definitions = useMemo(
    () => toColumnLayoutDefinitions(baseColumns),
    [baseColumns]
  )
  const resolvedColumns = useMemo(
    () => resolveColumnLayout(definitions, columnLayout),
    [definitions, columnLayout]
  )
  // 固定列の合計を表示幅より小さく保つ。測れるまでは上限なし
  const { ref: tableAreaRef, width: tableAreaWidth } =
    useElementWidth<HTMLDivElement>()
  const maxFixedWidth =
    tableAreaWidth === undefined
      ? undefined
      : tableAreaWidth - FIXED_WIDTH_MARGIN
  // レイアウト編集モード（確定版 §2.3）。編集中の並び・固定は下書きで持ち、「編集完了」で保存する
  const [layoutDraft, setLayoutDraft] = useState<ResolvedColumn[] | null>(null)
  const isLayoutEditing = layoutDraft !== null
  // 保存する側の列（保存値、編集中は下書き）。表示幅に合わせた補正は入れない
  const currentColumns = layoutDraft ?? resolvedColumns
  // 広い画面で保存した固定が収まらないときは、表示の上だけ右側の固定を外す。
  // 補正は表示にだけ当て、下書きにも保存値にも入れない（何も変えずに「編集完了」を押しても固定は変わらない）
  const fittedColumns = useMemo(
    () =>
      maxFixedWidth === undefined
        ? currentColumns
        : fitFixedColumns(currentColumns, maxFixedWidth),
    [currentColumns, maxFixedWidth]
  )
  // ドラッグ中の列幅。離すまでは保存せず、画面だけ追従させる
  const [resizing, setResizing] = useState<{
    key: string
    width: number
  } | null>(null)
  const displayedColumns = useMemo(
    () =>
      resizing
        ? resizeColumn(fittedColumns, resizing.key, resizing.width, {
            maxFixedWidth
          })
        : fittedColumns,
    [fittedColumns, resizing, maxFixedWidth]
  )
  const saveColumns = useCallback(
    (next: ResolvedColumn[]) =>
      setColumnLayout(toColumnLayout(columnLayout, next, definitions)),
    [columnLayout, setColumnLayout, definitions]
  )
  const columns = useMemo(
    () =>
      applyColumnLayout(
        baseColumns,
        displayedColumns,
        (key, width) => ({
          width,
          onResizeWidth: (nextWidth) => setResizing({ key, width: nextWidth }),
          onResizeEnd: (nextWidth) => {
            // 上限は表示中の固定で決め、変えるのは保存する側の列の幅だけ
            const widthInLimit =
              resizeColumn(fittedColumns, key, nextWidth, {
                maxFixedWidth
              }).find((column) => column.key === key)?.width ?? nextWidth
            const next = resizeColumn(currentColumns, key, widthInLimit)
            // 編集中は下書きに入れ、「編集完了」でまとめて保存する
            if (isLayoutEditing) setLayoutDraft(next)
            else saveColumns(next)
            setResizing(null)
          }
        }),
        { isLayoutEditing }
      ),
    [
      baseColumns,
      displayedColumns,
      fittedColumns,
      currentColumns,
      maxFixedWidth,
      isLayoutEditing,
      saveColumns
    ]
  )
  const labelOf = useCallback(
    (key: string) => {
      const title = baseColumns.find((column) => column.key === key)?.title
      return typeof title === 'string'
        ? title
        : (SETTING_LABEL_BY_KEY[key] ?? key)
    },
    [baseColumns]
  )
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false)

  const rowSelection: TableProps<ShipmentOrdersListExEntity>['rowSelection'] = {
    selectedRowKeys,
    // ページング・絞り込みを跨いで選択済み行を保持するため、現在の dataSource に無い
    // キーも AntD 内部キャッシュ (preserveRecordsRef) に残す
    preserveSelectedRowKeys: true,
    onChange: (keys, rows) => onSelectionChange(keys, rows),
    getCheckboxProps: (record: ShipmentOrdersListExEntity) => ({
      disabled: isEditing,
      name: record.orderNo
    })
  }

  return (
    <>
      <div className="flex items-center justify-end gap-2 px-2 pb-1">
        {isLayoutEditing ? (
          <>
            <span className="text-xs text-secondary">
              {DESCRIPTIONS.LAYOUT_EDIT_HELP}
            </span>
            <Button
              type="default"
              size="small"
              onClick={() => setLayoutDraft(null)}
            >
              {ACTIONS.CANCEL}
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                if (layoutDraft) saveColumns(layoutDraft)
                setLayoutDraft(null)
              }}
            >
              {ACTIONS.FINISH_LAYOUT_EDIT}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="default"
              size="small"
              icon={<FaGripVertical />}
              onClick={() => setLayoutDraft(resolvedColumns)}
            >
              {ACTIONS.LAYOUT_EDIT}
            </Button>
            <Button
              type="default"
              size="small"
              icon={<FaColumns />}
              onClick={() => setIsColumnSettingsOpen(true)}
            >
              {ACTIONS.COLUMN_SETTINGS}
            </Button>
          </>
        )}
      </div>
      <ShipmentOrdersColumnSettingsModal
        open={isColumnSettingsOpen}
        onClose={() => setIsColumnSettingsOpen(false)}
        columns={resolvedColumns}
        definitions={definitions}
        labelOf={labelOf}
        onSave={saveColumns}
        onReset={() => setColumnLayout(EMPTY_COLUMN_LAYOUT)}
      />
      <div ref={tableAreaRef}>
        <HeaderColumnDragArea
          columns={currentColumns}
          labelOf={labelOf}
          maxFixedWidth={maxFixedWidth}
          onMove={setLayoutDraft}
        >
          <Table
            size="small"
            rowKey="id"
            dataSource={shipmentOrders}
            pagination={false}
            sticky={{
              offsetHeader: stickyOffsetHeader
            }}
            columns={columns}
            components={TABLE_COMPONENTS}
            scroll={{
              x: sumVisibleWidth(displayedColumns)
            }}
            showSorterTooltip={false}
            onChange={handleTableChange}
            rowSelection={rowSelection}
            // ピン留め行は選択済み行でもあり AntD 標準の選択行背景 (cell 単位) が優先されて
            // 隠れてしまうため、セルに対して !important で上書きし常に視認できるようにする
            // 固定の範囲をいつも見せる: 固定列の見出しに色を付け、固定の最後の列の右に境界線を引く
            // （antd 標準は横スクロール時の影だけ）
            className="[&_.ant-table-cell-fix-left-last]:!border-r-2 [&_.ant-table-cell-fix-left-last]:!border-solid [&_.ant-table-cell-fix-left-last]:!border-r-primary [&_.ant-table-thead_.ant-table-cell-fix-left]:!bg-blue-50"
            rowClassName={(record) =>
              pinnedRowIds.has(record.id)
                ? '[&_.ant-table-cell]:!bg-amber-50'
                : ''
            }
          />
        </HeaderColumnDragArea>
      </div>
    </>
  )
}
